import routes, { RouteOptions } from 'sitewide-js/core_pre_webpack/routes';
import $ from 'sitewide-jquery';
import { logError, MentionsFetchError } from './error';
import { Mention, Filter } from './common';
import getQSParams from 'js/utils/getQSParams';

const { top_mention_ids } = getQSParams('top_mention_ids');

interface SetPublicityResult {
  id: number;
  is_public: boolean;
  is_hidden?: undefined;
}

export const setPublicity = ({
  id,
  is_public,
  citation,
}: {
  id: number;
  is_public: boolean;
  citation: boolean;
}): Promise<SetPublicityResult> => {
  return new Promise((res, rej) => {
    $.ajax({
      type: 'PUT',
      url: routes.set_publicity_api_v0_mentions_path({
        subdomain_param: 'api',
        id,
        is_publically_viewable: is_public,
        citation,
      }),
    })
      .fail(() => rej({ id, is_public }))
      .done(() => res({ id, is_public }));
  });
};

interface SetHiddennessResult {
  id: number;
  is_public?: undefined;
  is_hidden: boolean;
}

export const setHiddenness = ({
  id,
  is_hidden,
  citation,
}: {
  id: number;
  is_hidden: boolean;
  citation: boolean; // is this right?
}): Promise<SetHiddennessResult> => {
  return new Promise((res, rej) => {
    $.ajax({
      type: 'PUT',
      url: routes.set_hiddenness_api_v0_mentions_path({
        subdomain_param: 'api',
        id,
        hidden: is_hidden,
        citation,
      }),
    })
      .fail(rej)
      .done(() => res({ id, is_hidden }));
  });
};

export type SetMentionResult = SetPublicityResult | SetHiddennessResult;

const getApiRouteName = (currentFilter: Filter) => {
  switch (currentFilter) {
    case 'all':
      return { visibility: 'not_hidden' };
    case 'high_confidence':
      return { visibility: 'not_hidden', probably_you: true };
    case 'not_me':
      return { visibility: 'hidden' };
    case 'is_me':
      return { visibility: 'public' };
  }
};

export interface Meta {
  counts: Record<Filter, number>;
  certification_counts: Record<number, number>;
  recent_view_counts: Record<number, number>;
  section_title?: Record<number, string>;
  /** Timestamp */
  cursor?: string;
  cursor_id?: number | string;
  ordering_method?: 'confidence' | 'descending_id' | 'llm_score';
}

interface MentionsIndexResult {
  mentions: Mention[];
  meta: Meta;
}

export const getMentions = async ({
  currentFilter,
  cursorId,
  mentionedUserId,
  skipCounts,
  sortBy,
}: {
  currentFilter: Filter;
  cursorId?: number | string;
  mentionedUserId?: number;
  skipCounts: boolean;
  sortBy?: 'recency' | 'relevance';
}): Promise<MentionsIndexResult> => {
  const params: RouteOptions = {
    ...getApiRouteName(currentFilter),
    subdomain_param: 'api',
    for_user_id: mentionedUserId || window?.Aedu?.User?.current.id,
    skip_counts: skipCounts,
  };
  if (sortBy) {
    params.sort_by = sortBy;
  }
  if (top_mention_ids) {
    params.top_mention_ids = top_mention_ids;
  }
  if (cursorId) {
    params.cursor_id = cursorId;
  }
  const url = routes.api_v0_mentions_path(params);
  const res = await fetch(url);

  if (!res.ok) {
    logError(await res.text(), res.status, url);
    throw new MentionsFetchError();
  }

  return res.json();
};

export const getMentionCounts = async (mentionedUserId = null) => {
  const params = {
    subdomain_param: 'api',
    for_user_id: mentionedUserId || window?.Aedu?.User?.current.id,
  };
  const res = await fetch(routes.premium_counts_api_v0_mentions_path(params));
  return res.json();
};

export const getMention = async (id: number) => {
  const url = routes.api_v0_mention_path({
    subdomain_param: 'api',
    id,
  });
  const res = await fetch(url);

  if (!res.ok) {
    logError(await res.text(), res.status, url);
    throw new MentionsFetchError();
  }

  return res.json() as Promise<Mention>;
};
